import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { changeProgramType } from 'legacy/lut/features/partner360/configuration'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import {
  ATTENTION_STATUS_BITS,
  getAttentionStatusRole,
} from 'partner360/entities/attentionStatuses'

import {
  setCockpitOffering,
  setCockpitSelectedAttentionStatus,
} from '../../../model/financialCockpitSlice'
import {
  selectCockpitAllOfferingsAttentionStatuses,
  selectCockpitAttentionStatuses,
  selectCockpitOffering,
  selectCockpitSelectedAttentionStatusBit,
  selectCockpitSelectedAttentionStatusOffering,
  selectCockpitUserAccess,
} from '../../../model/selectors'
import { sortAttentionItemsBySeverity } from '../lib'
import type { IAttentionStatusesItem, IAttentionStatusesTile } from '../types'

import { useAttentionDrillMode } from './useAttentionDrillMode'
import { STATUS_UNIT, useAttentionStatusesText } from './useAttentionStatusesText'

/** A row's identity: the status' bit and the offering that raised it. `Overall` lists the same
 *  status once per offering, so the bit alone no longer names one row. */
const toItemId = (bitValue: number, offering: string | undefined) => `${bitValue}|${offering ?? ''}`

/* Statuses whose detail lists the same status raised by the partner's other offerings. */
const CROSS_OFFERING_BITS: number[] = [ATTENTION_STATUS_BITS.planned]

/**
 * The single-partner cockpit's tile: the partner's raised statuses, and the picked one as the
 * detail leaf. Selection lives in the slice rather than in component state because the collapsed
 * and the expanded tile are separate component trees.
 */
export const useCockpitAttentionStatuses = (): IAttentionStatusesTile => {
  const dispatch = useAppDispatch()
  const text = useAttentionStatusesText()
  const isOwnDrill = useAttentionDrillMode('single-partner')
  const statuses = useSelector(selectCockpitAttentionStatuses)
  const allOfferingsStatuses = useSelector(selectCockpitAllOfferingsAttentionStatuses)
  const cockpitOffering = useSelector(selectCockpitOffering)
  const selectedBit = useSelector(selectCockpitSelectedAttentionStatusBit)
  const selectedOffering = useSelector(selectCockpitSelectedAttentionStatusOffering)
  const { role } = useSelector(selectCockpitUserAccess)
  const statusRole = getAttentionStatusRole(role)

  const selected = isOwnDrill
    ? (statuses.find(
        ({ bitValue, offerings }) =>
          bitValue === selectedBit && (offerings[0] ?? null) === (selectedOffering ?? null)
      ) ?? null)
    : null

  /* The detail reads the same title as the row it was opened from, placeholders filled. */
  const selectedStatus = selected && { ...selected, title: text.statusTitle(selected) }

  const items = useMemo<IAttentionStatusesItem[]>(
    () =>
      sortAttentionItemsBySeverity(
        statuses.map((status) => ({
          id: toItemId(status.bitValue, status.offerings[0]),
          title: text.statusTitle(status),
          severity: status.severity,
          actionLabel: text.phrase.actionLabel(status.bitValue, statusRole),
          score: status.score,
          /* One partner's own status counts nothing — it is raised or it is not — so the card's
             ring carries the level rather than a number. */
          marksSeverity: true,
          /* The status list is the only level, so picking a status opens its detail. */
          opensDetail: true,
          bitValue: status.bitValue,
          offering: status.offerings[0] ?? null,
        }))
      ),
    [statuses, statusRole, text]
  )

  const selectItem = useCallback(
    (id: string) => {
      const [bitValue, offering] = id.split('|')

      dispatch(
        setCockpitSelectedAttentionStatus({
          bitValue: Number(bitValue),
          offering: offering || null,
        })
      )
    },
    [dispatch]
  )

  const goToLevel = useCallback(() => dispatch(setCockpitSelectedAttentionStatus(null)), [dispatch])

  /* The same status raised by the partner's other offerings. `Overall` already lists every
     offering, so only an offering's own tab offers them. */
  const relatedStatuses = useMemo<IAttentionStatusesItem[]>(() => {
    if (
      !selected ||
      cockpitOffering === ProgramTypes.Overall ||
      !CROSS_OFFERING_BITS.includes(selected.bitValue)
    ) {
      return []
    }

    return sortAttentionItemsBySeverity(
      allOfferingsStatuses
        .filter(
          ({ bitValue, offerings }) =>
            bitValue === selected.bitValue && offerings[0] && offerings[0] !== cockpitOffering
        )
        .map((status) => ({
          id: toItemId(status.bitValue, status.offerings[0]),
          title: text.statusTitle(status),
          severity: status.severity,
          actionLabel: text.phrase.actionLabel(status.bitValue, statusRole),
          opensDetail: true,
        }))
    )
  }, [allOfferingsStatuses, cockpitOffering, selected, statusRole, text])

  /* A related status belongs to another offering, so its tab opens with the status' detail. */
  const selectRelatedStatus = useCallback(
    (id: string) => {
      const [bitValue, offering] = id.split('|')

      if (offering) {
        dispatch(changeProgramType(offering as ProgramTypes))
        dispatch(setCockpitOffering(offering as ProgramTypes))
      }

      dispatch(
        setCockpitSelectedAttentionStatus({
          bitValue: Number(bitValue),
          offering: offering || null,
        })
      )
    },
    [dispatch]
  )

  return {
    items,
    /* The payload reports what the partner raised and never how many statuses exist, so the
       status level has no denominator to count against. */
    unit: STATUS_UNIT,
    crumbs: [],
    detail: selectedStatus ? { status: selectedStatus, relatedStatuses } : null,
    selectItem,
    goToLevel,
    selectRelatedStatus,
  }
}
